import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical, Upload } from 'lucide-react';

import {
  Banner,
  DropdownMenu,
  Headline,
  HeadlineAndSubheadlineContainer,
  LinkButton,
  LinkButtonContainer,
  LogoContainer,
  MenuButton,
  MenuItem,
  SubHeadline,
} from './styles';
import {
  CropConnectRoleDefinitions,
  WidgetDto,
} from '../../../../services/cropConnectApiClient';
import { securityTrimConditionsMet } from '../../../../services/auth/SecurityTrim';
import { useContextStore } from '../../../../stores/contextStore';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';

type UploadBannerProps = {
  bannerWidget: WidgetDto;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onBannerClick: () => void;
};

const UploadBanner: React.FC<UploadBannerProps> = ({
  bannerWidget,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  onBannerClick,
}) => {
  const { t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const {
    globalAssignment,
    nordzuckerInternalUser,
    businessPartnerAssignment,
    isAccessOnBehalf,
    isContextReady,
  } = useContextStore();

  const [isAdminView, setIsAdminView] = useState(
    !isAccessOnBehalf &&
      securityTrimConditionsMet(
        {
          requiredGlobalRoles: [CropConnectRoleDefinitions.DocumentPublisher],
        },
        globalAssignment,
        businessPartnerAssignment,
        nordzuckerInternalUser
      )
  );

  // Handle clicks outside the menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (isContextReady()) {
      setTimeout(() => {
        setIsAdminView(
          !isAccessOnBehalf &&
            securityTrimConditionsMet(
              {
                requiredGlobalRoles: [
                  CropConnectRoleDefinitions.DocumentPublisher,
                ],
              },
              globalAssignment,
              businessPartnerAssignment,
              nordzuckerInternalUser
            )
        );
      }, 2000);
    }
  }, [bannerWidget]);

  return (
    <Banner
      $color={bannerWidget.propertyBag?.color}
      onClick={() => onBannerClick()}
    >
      {isAdminView && (
        <MenuButton
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
        >
          <MoreVertical color="black" size={20} />
        </MenuButton>
      )}

      {isMenuOpen && (
        <DropdownMenu ref={menuRef}>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onMoveUp();
            }}
          >
            {t(TranslationKeys.general_document_move_up)}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onMoveDown();
            }}
          >
            {t(TranslationKeys.general_document_move_down)}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onEdit();
            }}
          >
            {t(TranslationKeys.general_document_edit)}
          </MenuItem>
          <MenuItem
            className="delete"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onDelete();
            }}
          >
            {t(TranslationKeys.general_document_delete)}
          </MenuItem>
        </DropdownMenu>
      )}
      <LogoContainer>
        <Upload size={44} />
      </LogoContainer>
      <HeadlineAndSubheadlineContainer>
        <Headline>
          {
            bannerWidget.propertyBag?.headlines.find(
              (headline) => headline.type === 'HEADLINE'
            )?.text
          }
        </Headline>
        <SubHeadline>
          {
            bannerWidget.propertyBag?.headlines.find(
              (headline) => headline.type === 'SUB_HEADLINE'
            )?.text
          }
        </SubHeadline>
      </HeadlineAndSubheadlineContainer>
      <LinkButtonContainer>
        <LinkButton>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </LinkButton>
      </LinkButtonContainer>
    </Banner>
  );
};

export default UploadBanner;
